import { store } from '@/store/store'
import { originalApi } from '@/store/api/originalApi'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import {
	applyChickenRoundState,
	selectChickenGame,
	setChickenAnimating,
	setChickenAutoBetRunning,
	setChickenAutoBetSettings,
	setChickenMultiplierSteps,
	setChickenSubmitting,
} from '@/store/slices/chickenSlice'
import { showApiErrorToast } from '@/lib/api-toast'
import { extractApiErrorMessage } from '@/lib/api-message'
import { createIdempotencyKey } from '@/lib/idempotency-key'
import type { ChickenAdvanceData, ChickenAutoBetData, ChickenDifficulty, ChickenRoundState } from '@/types/chicken'
import { SoundManager } from './SoundManager'
import type { ChickenScene } from './ChickenScene'

const ANIMATION_SETTLE_MS = 700
const AUTO_BET_RESULT_DELAY_MS = 700
const AUTO_BET_ROUND_DELAY_MS = 500

export class GameController {
	private disposed = false
	private scene: ChickenScene | null = null
	private autoBetRunning = false
	private autoBetTimer: ReturnType<typeof setTimeout> | null = null
	private remainingAutoBets = 0
	private autoBetSessionProfit = 0

	constructor(scene?: ChickenScene | null) {
		this.scene = scene ?? null
		if (scene) {
			const state = this.chickenState
			scene.rebuildLevel(state.difficulty, state.multiplierSteps)
			scene.setChickenStep(state.step, false)
		}
		void this.loadActiveRound()
	}

	dispose() {
		this.disposed = true
		this.stopAutoBet()
		this.scene = null
	}

	private get chickenState() {
		return selectChickenGame(store.getState())
	}

	private get authenticated() {
		return selectIsAuthenticated(store.getState())
	}

	private requireAuth(message: string) {
		if (this.authenticated) {
			return true
		}

		showApiErrorToast(message)
		return false
	}

	private applyRoundState(state: ChickenRoundState) {
		store.dispatch(applyChickenRoundState(state))
		if (state.status === 'win') SoundManager.instance.play('win')
	}

	private roundFromAdvance(data: ChickenAdvanceData | ChickenAutoBetData): ChickenRoundState {
		if (data.status === 'win') {
			return {
				status: 'win',
				step: data.step,
				multiplier: data.result.multiplier,
				payout: data.result.payout,
				result: data.result,
			}
		}

		if (data.status === 'lost') {
			return {
				status: 'lost',
				step: data.step,
				multiplier: data.multiplier,
				payout: data.payout,
				result: data.result,
			}
		}

		return {
			status: 'bet',
			step: data.step,
			multiplier: data.multiplier,
			payout: data.payout,
		}
	}

	async loadMultipliers(difficulty: ChickenDifficulty) {
		try {
			const request = store.dispatch(originalApi.endpoints.getChickenMultipliers.initiate({ difficulty }))
			const response = await request.unwrap()
			request.unsubscribe()

			if (this.disposed) {
				return
			}

			// Ignore stale responses if the player already switched difficulty.
			if (this.chickenState.difficulty !== difficulty && this.chickenState.phase !== 'live') {
				return
			}

			const steps = response.data.steps
			store.dispatch(setChickenMultiplierSteps(steps))
			this.scene?.setMultiplierTable(steps)
		} catch (error) {
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to load chicken multipliers.')
		}
	}

	private async loadActiveRound() {
		if (!this.authenticated || this.disposed) {
			return
		}

		const request = store.dispatch(originalApi.endpoints.getChickenActiveRound.initiate())
		try {
			const response = await request.unwrap()
			if (this.disposed) {
				return
			}

			const { chickenGame, step, multiplier, payout } = response.data
			if (!chickenGame) {
				return
			}

			await this.loadMultipliers(chickenGame.difficulty)
			this.scene?.rebuildLevel(chickenGame.difficulty, this.chickenState.multiplierSteps)
			this.scene?.setChickenStep(step, false)
			this.scene?.setMultiplierLabel(multiplier)

			store.dispatch(
				applyChickenRoundState({
					status: 'bet',
					chickenGame,
					step,
					multiplier,
					payout,
				}),
			)
		} catch (error) {
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to load the active chicken game.')
		} finally {
			request.unsubscribe()
		}
	}

	async startGame() {
		if (this.chickenState.autoBetRunning || this.chickenState.betMode === 'auto') {
			return
		}
		if (!this.requireAuth('Sign in to place a bet.')) {
			return
		}

		const amount = Number(this.chickenState.betAmount)
		if (!Number.isFinite(amount) || amount <= 0) {
			showApiErrorToast('Enter a valid bet amount.')
			return
		}

		this.scene?.resetRound()
		store.dispatch(setChickenSubmitting(true))
		store.dispatch(setChickenAnimating(true))

		try {
			const response = await store
				.dispatch(
					originalApi.endpoints.startChickenRound.initiate({
						currency_code: this.chickenState.currencyCode,
						amount,
						difficulty: this.chickenState.difficulty,
						idempotency_key: createIdempotencyKey(),
					}),
				)
				.unwrap()

			await this.playOutcome(response.data)
		} catch (error) {
			store.dispatch(setChickenSubmitting(false))
			store.dispatch(setChickenAnimating(false))
			this.scene?.setChickenStep(this.chickenState.step, false)
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to start the chicken round.')
		}
	}

	async stepForward() {
		if (this.chickenState.autoBetRunning) {
			return
		}

		if (this.chickenState.phase !== 'live' || this.chickenState.isSubmitting || this.chickenState.isAnimating) {
			return
		}

		store.dispatch(setChickenSubmitting(true))
		store.dispatch(setChickenAnimating(true))

		try {
			const response = await store.dispatch(originalApi.endpoints.stepChickenRound.initiate()).unwrap()
			await this.playOutcome(response.data)
		} catch (error) {
			store.dispatch(setChickenSubmitting(false))
			store.dispatch(setChickenAnimating(false))
			this.scene?.setChickenStep(this.chickenState.step, false)
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to advance the chicken.')
		}
	}

	private async playOutcome(data: ChickenAdvanceData) {
		SoundManager.instance.play('jump')
		this.scene?.playJumpPreview()

		if (data.status === 'bet') {
			await this.scene?.playSurviveToStep(data.step)
			this.applyRoundState(this.roundFromAdvance(data))
			store.dispatch(setChickenAnimating(false))
			return
		}

		if (data.status === 'win') {
			await this.scene?.playSurviveToStep(data.step)
			await this.scene?.playFinishWin()
			this.applyRoundState(this.roundFromAdvance(data))
			store.dispatch(setChickenAnimating(false))
			return
		}

		SoundManager.instance.play('carBrake')
		await this.scene?.playDeathAtStep(data.step)
		this.applyRoundState(this.roundFromAdvance(data))
		store.dispatch(setChickenAnimating(false))
	}

	async cashOut() {
		if (this.chickenState.autoBetRunning) {
			return
		}

		if (this.chickenState.phase !== 'live' || this.chickenState.isSubmitting || this.chickenState.isAnimating) {
			return
		}

		if (this.chickenState.step <= 0) {
			showApiErrorToast('Cross at least one road before cashing out.')
			return
		}

		store.dispatch(setChickenSubmitting(true))

		try {
			const response = await store.dispatch(originalApi.endpoints.cashOutChickenRound.initiate()).unwrap()
			SoundManager.instance.play('cashout')
			await this.delay(ANIMATION_SETTLE_MS)
			this.applyRoundState(this.roundFromAdvance(response.data))
		} catch (error) {
			store.dispatch(setChickenSubmitting(false))
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to cash out.')
		}
	}

	async startAutoBet() {
		if (this.autoBetRunning || this.chickenState.isSubmitting || this.chickenState.phase === 'live') {
			return
		}

		if (!this.requireAuth('Sign in to place a bet.')) {
			return
		}

		const amount = Number(this.chickenState.betAmount)
		if (!Number.isFinite(amount) || amount <= 0) {
			showApiErrorToast('Enter a valid bet amount.')
			return
		}

		const totalRoads = this.chickenState.multiplierSteps.length
		const steps = this.chickenState.autoBetSettings.steps
		if (!Number.isInteger(steps) || steps < 1 || (totalRoads > 0 && steps > totalRoads)) {
			showApiErrorToast('Select how many roads to cash out on.')
			return
		}

		this.autoBetRunning = true
		this.autoBetSessionProfit = 0
		this.remainingAutoBets = this.chickenState.autoBetSettings.isInfinite ? Number.POSITIVE_INFINITY : this.chickenState.autoBetSettings.betCount
		store.dispatch(setChickenAutoBetRunning(true))
		this.scheduleAutoBetRound(AUTO_BET_ROUND_DELAY_MS)
	}

	stopAutoBet() {
		this.autoBetRunning = false
		this.autoBetSessionProfit = 0
		this.clearAutoBetTimer()
		store.dispatch(setChickenAutoBetRunning(false))
		store.dispatch(setChickenSubmitting(false))
	}

	private clearAutoBetTimer() {
		if (this.autoBetTimer) {
			clearTimeout(this.autoBetTimer)
			this.autoBetTimer = null
		}
	}

	private scheduleAutoBetRound(delayMs: number) {
		this.clearAutoBetTimer()
		this.autoBetTimer = setTimeout(() => {
			void this.runAutoBetRound()
		}, delayMs)
	}

	private async runAutoBetRound() {
		if (!this.autoBetRunning || this.disposed) {
			this.stopAutoBet()
			return
		}

		this.scene?.resetRound()
		store.dispatch(setChickenSubmitting(true))
		store.dispatch(setChickenAnimating(true))

		try {
			const response = await store
				.dispatch(
					originalApi.endpoints.placeChickenAutoBet.initiate({
						currency_code: this.chickenState.currencyCode,
						amount: Number(this.chickenState.betAmount),
						difficulty: this.chickenState.difficulty,
						steps: this.chickenState.autoBetSettings.steps,
						idempotency_key: createIdempotencyKey(),
					}),
				)
				.unwrap()

			await this.playAutoOutcome(response.data)

			if (!this.autoBetRunning || this.disposed) {
				return
			}

			this.autoBetSessionProfit += response.data.result.profit

			const { stopOnProfit, stopOnLoss, isInfinite } = this.chickenState.autoBetSettings
			if (stopOnProfit > 0 && this.autoBetSessionProfit >= stopOnProfit) {
				this.stopAutoBet()
				return
			}

			if (stopOnLoss > 0 && this.autoBetSessionProfit <= -stopOnLoss) {
				this.stopAutoBet()
				return
			}

			if (!isInfinite) {
				this.remainingAutoBets -= 1
				store.dispatch(
					setChickenAutoBetSettings({
						betCount: Math.max(0, this.remainingAutoBets),
					}),
				)

				if (this.remainingAutoBets <= 0) {
					this.stopAutoBet()
					return
				}
			}

			this.scheduleAutoBetRound(AUTO_BET_RESULT_DELAY_MS + AUTO_BET_ROUND_DELAY_MS)
		} catch (error) {
			this.stopAutoBet()
			store.dispatch(setChickenAnimating(false))
			this.scene?.resetRound()
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to place auto bet.')
		}
	}

	private async playAutoOutcome(data: ChickenAutoBetData) {
		SoundManager.instance.play('jump')
		this.scene?.playJumpPreview()

		if (data.status === 'lost') {
			SoundManager.instance.play('carBrake')
			await this.scene?.playDeathAtStep(data.step)
			this.applyRoundState(this.roundFromAdvance(data))
			store.dispatch(setChickenAnimating(false))
			return
		}

		await this.scene?.playSurviveToStep(data.step)
		if (data.step >= this.chickenState.multiplierSteps.length) {
			await this.scene?.playFinishWin()
		} else {
			SoundManager.instance.play('cashout')
		}

		this.applyRoundState(this.roundFromAdvance(data))
		store.dispatch(setChickenAnimating(false))
	}

	private delay(ms: number) {
		return new Promise<void>((resolve) => {
			window.setTimeout(resolve, ms)
		})
	}
}
